import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { ARTICLES, ARTICLE_IMAGES, IMAGES } from "@/data/site";
import Seo from "@/components/Seo";

const CATEGORIES = [
  "All",
  "Development Applications",
  "Complying Development",
  "Exempt Development",
  "Certificates",
  "Zoning & LEP",
  "Bushfire & Flood",
  "Rural & Agritourism",
  "Commercial & Industrial",
];

const LEVEL_STYLE = {
  Beginner: "bg-seaglass-light text-teal",
  Intermediate: "bg-sand-light text-teal",
  Pro: "bg-teal text-ivory",
};

const Hub = () => {
  const [cat, setCat] = useState("All");

  const results = useMemo(
    () => ARTICLES.filter((a) => cat === "All" || a.category === cat),
    [cat]
  );

  return (
    <div data-testid="planning-hub-page">
      <Seo
        title="Planning Hub — Plain-English NSW Planning Guides"
        description="Browse plain-English guides on DAs, complying development, zoning, planning certificates and more — the NSW planning system explained before you commit."
      />
      <section className="bg-sand-light py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow>The Planning Hub</Eyebrow>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-teal">
            NSW planning, in plain English
          </h1>
          <p className="mt-5 max-w-2xl text-charcoal/70 leading-relaxed">
            A free, growing library explaining development applications, complying development,
            certificates, zoning, constraints and the assessment process — organised by topic,
            not legislation. Pick a topic below to get started.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2" data-testid="hub-category-pills">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`hub-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-xs font-mono uppercase tracking-[0.14em] px-4 py-2 rounded-full border transition-colors duration-300 ${
                  cat === c
                    ? "bg-teal text-ivory border-teal"
                    : "bg-white text-teal border-cloud hover:border-teal"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <p className="mt-8 text-sm text-charcoal/60" data-testid="hub-result-count">
            {results.length} guide{results.length === 1 ? "" : "s"}
            {cat !== "All" ? ` in ${cat}` : ""}
          </p>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map((a, i) => (
              <FadeIn key={a.slug} delay={(i % 3) * 0.06} className="h-full">
                <Link
                  to={`/hub/${a.slug}`}
                  data-testid={`hub-article-${a.slug}`}
                  className="group flex flex-col h-full bg-white border border-cloud hover:border-teal hover:-translate-y-1 hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-sand-light">
                    <img
                      src={IMAGES[ARTICLE_IMAGES[a.slug]]}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] font-mono uppercase tracking-[0.14em] px-2.5 py-1 ${LEVEL_STYLE[a.level]}`}
                    >
                      {a.level}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal/50">
                      {a.category}
                    </span>
                  </div>
                  <h2 className="mt-4 font-serif text-xl text-teal leading-snug tracking-tight">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-sm text-charcoal/70 leading-relaxed flex-1">
                    {a.excerpt}
                  </p>
                  <div className="mt-6 flex items-center justify-between text-xs text-charcoal/50">
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {a.minutes} min read
                    </span>
                    <span className="inline-flex items-center gap-1 text-teal font-mono uppercase tracking-[0.14em]">
                      Read <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>

          {!results.length && (
            <div className="mt-10 bg-white border border-cloud p-10 text-center">
              <p className="font-serif text-xl text-teal">Nothing here — yet.</p>
              <p className="mt-2 text-sm text-charcoal/70">
                Try another topic, or{" "}
                <Link to="/contact" className="underline underline-offset-4">
                  ask us directly
                </Link>
                . Your question may become our next guide.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Hub;
